import React from 'react'
import ReactPaginate from 'react-paginate'
import { CollectionsLoadingState } from '~/components/CollectionsLoadingState'
import { CollectionsErrorState } from '~/components/CollectionsErrorState'
import { DropVaultCard } from '~/components/DropVaultCard'
import { NftCard } from '~/components/NftCard'
import { Collection } from '~/domain/Collection'
import { Contract } from '~/domain/Contract'
import { Vault, VaultWithCollectionAndTokenId } from '~/domain/Vault'
import { useItems } from '~/hooks/useItems'
import styles from './index.module.scss'
import paginationStyles from '~/components/Pagination/index.module.scss'
import emptySeriesStyles from '~/components/SeriesPage/SeriesCollectionsEmptyState/index.module.scss'

interface NftCollectionProps {
  nftsPerPage: number
  collection: Collection
  tokenId?: string
  vault?: Vault
}

export const NftCollection: React.FC<NftCollectionProps> = ({ collection, tokenId, vault, nftsPerPage }) => {
  const [offset, setOffset] = React.useState<number>(0)
  const [activePage, setActivePage] = React.useState<number>(0)
  const { total, data: items, isLoading, isError } = useItems(offset, nftsPerPage, collection, tokenId, vault)
  const pageCount = total
    ? Math.ceil(total.toNumber() / nftsPerPage)
    : 0

  const handlePageClick = (event: { selected: number }) => {
    if (total?.gt(0)) {
      const newOffset = (event.selected * nftsPerPage) % total.toNumber()

      setOffset(newOffset)
      setActivePage(event.selected)
    }
  }

  if (isLoading) {
    return (
      <div data-testid="nft-collection-loading">
        <CollectionsLoadingState />
      </div>
    )
  }

  if (isError) {
    console.error(`Error loading contract "${collection.contract.address}" for series "${collection.seriesId}"`)

    return (
      <div data-testid="nft-collection-error">
        <CollectionsErrorState href='' />
      </div>
    )
  }

  if (total && total.eq(0)) {
    return (
      <div
        data-testid="nft-collection-empty"
        className={ emptySeriesStyles['series-collections-empty-state__card'] }
      >
        <strong className={ emptySeriesStyles['series-collections-empty-state__title'] }>
          Coming soon!
        </strong>

        <span className={ emptySeriesStyles['series-collections-empty-state__description'] }>
          Waiting for athletes to start minting their trophies.
        </span>
      </div>
    )
  }

  const dropVaultItems = items?.filter((item) => item.vault?.isDrop === true)
  const baseVaultItems = items?.filter((item) => item.vault && item.vault.isDrop === false)
  const nftItems = items?.filter((item) => item.vault === undefined)


  const baseVaultCards = baseVaultItems?.map((item) => {
    const vault: VaultWithCollectionAndTokenId = {
      seriesId: item.vault?.seriesId as string,
      type: item.vault?.type as 'trophy',
      contract: item.vault?.contract as Contract,
      creator: item.vault?.creator as string,
      fragments: item.vault?.fragments as number,
      isDrop: false,
      tokenId: item.tokenId,
      collection: collection,
    }

    return (
      <NftCard
        key={ item.nft.metadata.id }
        collection={ vault }
        nft={ item }
      />
    )
  })

  const dropVaultCards = dropVaultItems?.map((item) => {
    const vault: VaultWithCollectionAndTokenId = {
      seriesId: item.vault?.seriesId as string,
      type: item.vault?.type as 'trophy',
      contract: item.vault?.contract as Contract,
      creator: item.vault?.creator as string,
      fragments: item.vault?.fragments as number,
      isDrop: true,
      tokenId: item.tokenId,
      collection: collection,
    }

    return (
      <DropVaultCard
        key={ item.nft.metadata.id }
        vault={ vault }
      />
    )
  })

  const nftCards = nftItems?.map((item) => {
    return (
      <NftCard
        key={ item.nft.metadata.id }
        collection={ collection }
        nft={ item }
      />
    )
  })

  return (
    <div>
      <div
        data-testid="nft-collection"
        className={ styles['nft-collection__container'] }
      >
        { dropVaultCards }
        { baseVaultCards }
        { nftCards }
      </div>

      {
        pageCount > 1 &&
          <ReactPaginate
            pageCount={ pageCount }
            forcePage={ activePage }
            marginPagesDisplayed={ 2 }
            pageRangeDisplayed={ 2 }
            renderOnZeroPageCount={ null }
            onPageChange={ handlePageClick }
            previousLabel="<"
            nextLabel=">"
            breakLabel="..."
            containerClassName={ paginationStyles['pagination__container'] }
            pageClassName={ paginationStyles['pagination__page'] }
            activeClassName={ paginationStyles['pagination__page__active'] }
            activeLinkClassName={ paginationStyles['pagination__page__active'] }
            previousClassName={ paginationStyles['pagination__page__arrows'] }
            nextClassName={ paginationStyles['pagination__page__arrows'] }
            disabledClassName={ paginationStyles['pagination__page__disabled'] }
          />
      }
    </div>
  )
}
